import { useState, type FormEvent } from 'react';
import { Clock, Plus, Trash2 } from 'lucide-react';
import { trpc } from '../lib/trpc.ts';
import { Badge } from '../components/Badge.tsx';
import { EmptyState } from '../components/EmptyState.tsx';
import { Modal } from '../components/Modal.tsx';
import { useConfirm } from '../components/ConfirmDialog.tsx';
import { useToast } from '../components/Toast.tsx';
import { Reveal } from '../components/motion.tsx';
import './Schedules.css';

const DOW = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

function formatMin(m: number): string {
  const h = Math.floor(m / 60).toString().padStart(2, '0');
  const min = (m % 60).toString().padStart(2, '0');
  return `${h}:${min}`;
}

export function Schedules() {
  const list = trpc.schedule.list.useQuery();
  const utils = trpc.useUtils();
  const confirm = useConfirm();
  const toast = useToast();
  const upsert = trpc.schedule.upsert.useMutation({
    onSuccess: () => utils.schedule.list.invalidate(),
  });
  const remove = trpc.schedule.remove.useMutation({
    onSuccess: () => utils.schedule.list.invalidate(),
  });
  const [showForm, setShowForm] = useState(false);

  async function onDelete(id: string, name: string) {
    const ok = await confirm({
      title: 'Delete schedule?',
      body: (
        <>
          This removes <strong>{name}</strong>. Access levels that reference it will lose this time
          window.
        </>
      ),
      confirmLabel: 'Delete',
      danger: true,
    });
    if (!ok) return;
    await remove.mutateAsync({ id });
    toast.success('Schedule deleted', name);
  }

  return (
    <div className="stack schedules-page">
      <Reveal>
        <header className="page-head">
          <div className="title">
            <span className="section-eyebrow">Access · Time windows</span>
            <h1>
              <span className="serif-accent">Schedules</span>
            </h1>
            <p className="muted">
              Define time windows. Access levels reference these to gate entry.
            </p>
          </div>
          <span className="spacer" />
          <button onClick={() => setShowForm(true)}>
            <Plus size={14} />
            New schedule
          </button>
        </header>
      </Reveal>

      <Reveal delay={0.05}>
        <div className="card">
          {list.isLoading ? (
            <div className="stack">
              {[1, 2].map((i) => (
                <div key={i} className="skel" style={{ height: 38 }} />
              ))}
            </div>
          ) : !list.data || list.data.length === 0 ? (
            <EmptyState
              icon={Clock}
              title="No schedules"
              body="A schedule is one or more time windows (e.g. 'Office hours: Mon–Fri 08:00–18:00')."
              action={
                <button onClick={() => setShowForm(true)}>
                  <Plus size={14} />
                  New schedule
                </button>
              }
            />
          ) : (
            <table className="table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Rules</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {list.data.map((s) => (
                  <tr key={s.id}>
                    <td className="cell-name">{s.name}</td>
                    <td className="row wrap">
                      {(
                        s.rules as Array<{
                          daysOfWeek: number[];
                          startMinute: number;
                          endMinute: number;
                        }>
                      ).map((r, i) => (
                        <Badge key={i} tone="info">
                          {r.daysOfWeek.map((d) => DOW[d]).join(',')} {formatMin(r.startMinute)}–
                          {formatMin(r.endMinute)}
                        </Badge>
                      ))}
                    </td>
                    <td>
                      <button
                        className="ghost"
                        aria-label={`Delete schedule ${s.name}`}
                        onClick={() => void onDelete(s.id, s.name)}
                      >
                        <Trash2 size={14} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </Reveal>

      <ScheduleForm
        open={showForm}
        pending={upsert.isPending}
        onClose={() => setShowForm(false)}
        onSave={async (input) => {
          await upsert.mutateAsync(input);
          setShowForm(false);
          toast.success('Schedule saved', input.name);
        }}
      />
    </div>
  );
}

interface FormInput {
  name: string;
  rules: Array<{ daysOfWeek: number[]; startMinute: number; endMinute: number }>;
}

function ScheduleForm({
  open,
  pending,
  onClose,
  onSave,
}: {
  open: boolean;
  pending: boolean;
  onClose: () => void;
  onSave: (input: FormInput) => Promise<void>;
}) {
  const [name, setName] = useState('Office hours');
  const [days, setDays] = useState<number[]>([1, 2, 3, 4, 5]);
  const [start, setStart] = useState('08:00');
  const [end, setEnd] = useState('18:00');

  function toMinutes(t: string) {
    const [h, m] = t.split(':').map(Number);
    return (h ?? 0) * 60 + (m ?? 0);
  }

  async function submit(e: FormEvent) {
    e.preventDefault();
    await onSave({
      name,
      rules: [
        {
          daysOfWeek: days,
          startMinute: toMinutes(start),
          endMinute: toMinutes(end),
        },
      ],
    });
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="New schedule"
      maxWidth={480}
      dismissable={!pending}
      footer={
        <>
          <button type="button" className="ghost" onClick={onClose}>
            Cancel
          </button>
          <button type="submit" form="schedule-form" disabled={pending}>
            Save
          </button>
        </>
      }
    >
      <form id="schedule-form" onSubmit={submit} className="stack">
        <label className="field">
          <span className="field-label">Name</span>
          <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Name" required />
        </label>
        <div className="field">
          <span className="field-label">Days</span>
          <div className="row wrap">
            {DOW.map((d, i) => (
              <button
                type="button"
                key={d}
                className={days.includes(i) ? 'day-pill' : 'day-pill secondary'}
                aria-pressed={days.includes(i)}
                onClick={() =>
                  setDays((ds) => (ds.includes(i) ? ds.filter((x) => x !== i) : [...ds, i].sort()))
                }
              >
                {d}
              </button>
            ))}
          </div>
        </div>
        <div className="field">
          <span className="field-label">Window</span>
          <div className="row">
            <input type="time" value={start} onChange={(e) => setStart(e.target.value)} required />
            <span className="dim">to</span>
            <input type="time" value={end} onChange={(e) => setEnd(e.target.value)} required />
          </div>
        </div>
      </form>
    </Modal>
  );
}
